import { useState } from "react";
import api from "@/lib/api";
import { errMsg } from "@/lib/format";
import { useCategories } from "@/hooks/useCategories";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, ArrowDownToLine, ArrowUpFromLine, Trash2 } from "lucide-react";

export default function EditTransactionDialog({ tx, accounts, onClose, onSaved }) {
  const { categories } = useCategories();
  const [type, setType] = useState(tx.type || "expense");
  const [amount, setAmount] = useState(String(tx.amount ?? 0));
  const [category, setCategory] = useState(tx.category || "Otros");
  const [description, setDescription] = useState(tx.description || "");
  const [date, setDate] = useState((tx.date || "").slice(0, 10));
  const [accountId, setAccountId] = useState(tx.account_id || "");
  const [loading, setLoading] = useState(false);

  // Una transacción vinculada a una operación compuesta no se toca por su cuenta:
  // deshacerla a medias deja la otra pierna viva y los saldos a medio revertir.
  // El backend lo rechaza con 409; esto sólo evita ofrecer un formulario que va
  // a fallar. La lista incluye ahora el pago de tarjeta y el ajuste de saldo,
  // que antes faltaban aquí y en la guarda del servidor.
  const COMPOSITE = {
    transfer_id: "una transferencia",
    card_payment_id: "un pago de tarjeta",
    debt_payment_id: "un abono a una deuda",
    recurring_payment_id: "un pago recurrente",
    balance_adjustment_id: "un ajuste de saldo",
  };
  const lockedField = Object.keys(COMPOSITE).find(k => tx[k]) || (tx.is_transfer ? "transfer_id" : null);
  const isLocked = !!lockedField;
  const isCardPurchase = !!tx.card_id && !isLocked;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        type, amount: Number(amount), category, description,
        date: date ? new Date(date).toISOString() : undefined,
        // Una compra con tarjeta no vive en ninguna cuenta: mandar account_id
        // haría que el backend la rechace.
        ...(isCardPurchase ? {} : { account_id: accountId }),
      };
      await api.patch(`/transactions/${tx.id}`, payload);
      toast.success("Transacción actualizada");
      onSaved?.();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  };

  const remove = async () => {
    const what = isCardPurchase
      ? "¿Eliminar esta compra? Se descontará de lo que debes en la tarjeta."
      : "¿Eliminar esta transacción? Se ajustará el saldo de la cuenta.";
    if (!window.confirm(what)) return;
    setLoading(true);
    try {
      await api.delete(`/transactions/${tx.id}`);
      toast.success("Eliminada");
      onSaved?.();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#15141b] border-white/10 text-white sm:max-w-md max-h-[92vh] overflow-y-auto" data-testid="edit-tx-dialog">
        <DialogHeader><DialogTitle className="text-white">Editar transacción</DialogTitle></DialogHeader>
        {isLocked ? (
          <div className="space-y-3 mt-3">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-sm text-amber-200">
              Este movimiento forma parte de {COMPOSITE[lockedField]}. Deshazlo desde su
              propia sección: borrarlo por separado descuadraría las cuentas.
            </div>
            <button onClick={onClose} className="w-full bg-white/5 border border-white/10 text-slate-300 py-3 rounded-xl text-sm">Cerrar</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-2 p-1 bg-white/5 rounded-xl">
              <button type="button" onClick={() => setType("income")} data-testid="edit-tx-type-income"
                className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium ${type === "income" ? "bg-emerald-500/20 text-emerald-300" : "text-slate-400"}`}>
                <ArrowDownToLine className="w-4 h-4"/> Ingreso
              </button>
              <button type="button" onClick={() => setType("expense")} data-testid="edit-tx-type-expense"
                className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium ${type === "expense" ? "bg-rose-500/20 text-rose-300" : "text-slate-400"}`}>
                <ArrowUpFromLine className="w-4 h-4"/> Gasto
              </button>
            </div>
            <div>
              <Lbl>Monto</Lbl>
              <input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required data-testid="edit-tx-amount"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-white text-xl font-bold"/>
            </div>
            <div>
              <Lbl>Categoría</Lbl>
              <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="edit-tx-category"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-white text-sm">
                {categories.filter(c => type === "income" ? c.type === "income" : c.type === "expense").map(c => (
                  <option key={c.id || c.value} value={c.value} className="bg-[#15141b]">{c.value}</option>
                ))}
              </select>
            </div>
            {isCardPurchase ? (
              <div>
                <Lbl>Pagado con</Lbl>
                <div className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-slate-300 text-sm" data-testid="edit-tx-card-source">
                  Tarjeta de crédito · {tx.currency || ""}
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Para pasarla a una cuenta, bórrala y regístrala de nuevo.
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <Lbl>Cuenta</Lbl>
                <select value={accountId} onChange={(e) => setAccountId(e.target.value)} required data-testid="edit-tx-account"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-white text-sm">
                  {accounts.map(a => <option key={a.id} value={a.id} className="bg-[#15141b]">{a.name} · {a.currency}</option>)}
                </select>
              </div>
            )}
            <div>
              <Lbl>Descripción</Lbl>
              <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Opcional" data-testid="edit-tx-desc"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm"/>
            </div>
            <div>
              <Lbl>Fecha</Lbl>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="edit-tx-date"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm"/>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-2">
              <button type="button" onClick={remove} disabled={loading} data-testid="edit-tx-delete"
                className="flex items-center justify-center gap-2 bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/30 text-rose-200 font-semibold py-3 rounded-xl text-sm">
                <Trash2 className="w-4 h-4"/> Eliminar
              </button>
              <button type="submit" disabled={loading} data-testid="edit-tx-submit"
                className="flex items-center justify-center gap-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-semibold py-3 rounded-xl text-sm">
                {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : "Guardar"}
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Lbl({ children }) {
  return <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1.5">{children}</div>;
}
